import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sleep } from '../../util/async';
import { logger } from '../../util/logger';
import {
  useDirectConnectGetTaskLazy,
  useDirectConnectRemoveTask,
} from '../cache/direct-connect-task-cache';
import {
  DirectConnectTask,
  DirectConnectTaskType,
} from '../model/direct-connect.model';
import {
  DirectConnectGateSource,
  useDirectConnectGateKeeper,
} from './direct-connect-gate-keeper';
import { useDirectConnectHealthGateContext } from './direct-connect-health-gate';

export interface DirectConnectTaskExecutionResponse {
  success: boolean;
  message?: string;
}

export interface DirectConnectTaskNotFoundResponse {
  success: boolean;
  message?: string;
}

export interface DirectConnectTaskGateProps {
  children: ReactNode;
  loaderMessage: string;
  defaultErrorMessage: string;
  source: DirectConnectGateSource;
  taskType: DirectConnectTaskType;
  executeTask: (
    task: DirectConnectTask
  ) => Promise<DirectConnectTaskExecutionResponse>;
  onTaskNotFound?: () => Promise<DirectConnectTaskNotFoundResponse>;
  onSuccess?: () => Promise<void>;
  skip?: boolean;
}

const DirectConnectTaskGate = (props: DirectConnectTaskGateProps) => {
  const {
    children,
    defaultErrorMessage,
    loaderMessage,
    taskType,
    skip,
    executeTask,
    onTaskNotFound,
    onSuccess,
    source,
  } = props;

  const { i18n } = useTranslation();
  const { healthStatus } = useDirectConnectHealthGateContext();

  const [hasFoundTask, setHasFoundTask] = useState(false);
  const [isExecutionComplete, setIsExecutionComplete] = useState(skip);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>(defaultErrorMessage);
  const [hasExecutedOnce, setHasExecutedOnce] = useState(false);

  const { reportGateStatus } = useDirectConnectGateKeeper();

  const serialNumber = healthStatus?.serialNumber as string;

  const getTaskLazy = useDirectConnectGetTaskLazy();
  const removeTask = useDirectConnectRemoveTask();

  const executeTaskInternal = useCallback(async () => {
    if (skip) {
      logger.info('skipping direct connect task execution', {
        taskType,
      });
      setHasExecutedOnce(true);
      return;
    }

    const task = await getTaskLazy(serialNumber, taskType);

    if (!task) {
      logger.info('no direct connect task found', {
        serialNumber,
        taskType,
      });

      if (typeof onTaskNotFound === 'function') {
        const response = await onTaskNotFound();

        if (!response.success) {
          setHasError(true);
          setErrorMessage(response.message ?? defaultErrorMessage);
        }
      }

      // work around ionic bug where loader can get stuck if going loading state
      // to non-loading state too quick
      await sleep(100);

      setIsExecutionComplete(true);
      setHasExecutedOnce(true);
      return;
    }

    setHasFoundTask(true);
    setIsExecutionComplete(false);

    try {
      logger.info('starting direct connect task execution', {
        serialNumber,
        taskType,
        hasTask: task !== undefined,
      });

      const response = await executeTask(task);

      logger.info('direct connect task execution complete', {
        serialNumber,
        taskType,
        response,
      });

      if (response.success) {
        await removeTask(serialNumber, taskType);

        if (typeof onSuccess === 'function') {
          await onSuccess();
        }
      } else {
        setHasError(true);
        setErrorMessage(response.message ?? defaultErrorMessage);
      }
    } catch (error) {
      logger.error('error executing direct connect task', { error });
      setHasError(true);
    }

    setHasExecutedOnce(true);
    setIsExecutionComplete(true);
  }, [
    serialNumber,
    taskType,
    getTaskLazy,
    executeTask,
    onTaskNotFound,
    defaultErrorMessage,
    skip,
    removeTask,
    onSuccess,
  ]);

  useEffect(() => {
    executeTaskInternal();
  }, [executeTaskInternal]);

  const showLoader = hasFoundTask && !isExecutionComplete;
  const showError = hasError && !hasExecutedOnce;
  const showBackground = showLoader || showError;
  const showChildren = (isExecutionComplete && !hasError) || hasExecutedOnce;

  useEffect(() => {
    logger.info(`direct connect task gate - ${taskType}`, {
      taskType,
      showLoader,
      showError,
      showBackground,
      showChildren,
      skip,
      hasFoundTask,
      hasExecutedOnce,
      isExecutionComplete,
    });
  }, [
    showLoader,
    showError,
    showBackground,
    showChildren,
    taskType,
    skip,
    hasFoundTask,
    hasExecutedOnce,
    isExecutionComplete,
  ]);

  useEffect(() => {
    reportGateStatus({
      source,
      failed: {
        show: showError,
        props: {
          retry: executeTaskInternal,
          title: i18n.t('common:direct_connect'),
          description: errorMessage,
        },
      },
      loader: {
        show: showLoader,
        message: loaderMessage,
      },
      background: {
        show: showBackground,
      },
    });
  }, [
    reportGateStatus,
    source,
    showError,
    executeTaskInternal,
    i18n,
    errorMessage,
    showLoader,
    loaderMessage,
    showBackground,
  ]);

  return <>{showChildren && children}</>;
};

export { DirectConnectTaskGate };
